import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { addDays, dueLabel, mondayOf, relativeDueDate, weekLabel, weekRangeLabel } from '../lib/date';
import {
  useAllHouseholdMembers,
  useAllHouseholdSettings,
  useHouseholdMembers,
} from '../hooks/useHousehold';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import {
  useMyAssignments,
  useCreateAssignment,
  useCompleteAssignment,
  useSkipAssignment,
  useShareAssignment,
  useCancelShare,
} from '../hooks/useChoreAssignments';
import { useAllChoreDefinitions } from '../hooks/useChoreDefinitions';
import { AssignmentStatus, type ChoreAssignment } from '@chore-project/shared';
import { useAuth } from '../hooks/useAuth';
import { Modal } from '../components/Modal';
import { MemberAvatar } from '../components/MemberAvatar';
import { ChoreTile } from '../components/ChoreTile';
import { EmptyState, NoHouseholdEmpty } from '../components/EmptyState';
import { useCelebration } from '../components/Celebration';
import { tapLight } from '../lib/haptics';
import { calcPoints } from '../lib/dashboard';
import { PRIORITY_META, LENGTH_META } from '../lib/chore-meta';
import { ChoreLength, ChorePriority } from '@chore-project/shared';

export function AssignmentsPage() {
  const { user } = useAuth();
  const { householdId: firstHouseholdId, households } = useSelectedHousehold();
  const householdIds = (households ?? []).map((h) => h.householdId);
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | ''>('');
  const { data: assignments, isLoading } = useMyAssignments(statusFilter || undefined);
  const createAssignment = useCreateAssignment();
  const completeAssignment = useCompleteAssignment();
  const skipAssignment = useSkipAssignment();
  const share = useShareAssignment();
  const cancelShare = useCancelShare();
  // Tasks span every household the user belongs to, so definitions, members
  // and settings are fetched across all of them.
  const { data: settingsById } = useAllHouseholdSettings(householdIds);
  const { data: defs } = useAllChoreDefinitions(householdIds);
  const { data: members } = useAllHouseholdMembers(householdIds);

  const { tada } = useCelebration();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [choreDefId, setChoreDefId] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0] ?? '');

  // The page shows one Mon–Sun week at a time, starting on the current one.
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const weekEnd = addDays(weekStart, 6);
  const thisMonday = mondayOf(new Date());
  const isCurrentWeek = weekStart === thisMonday;
  const weekTitle = weekLabel(weekStart);
  const weekRange = weekRangeLabel(weekStart);

  // The chore whose secondary-actions (⋯) sheet is open, and the chore being passed.
  const [menuFor, setMenuFor] = useState<ChoreAssignment | null>(null);
  const [passFor, setPassFor] = useState<ChoreAssignment | null>(null);
  const { data: pickerMembers } = useHouseholdMembers(passFor?.householdId ?? '');

  // Open the create modal when arriving from the quick-add (+) sheet.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowModal(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleComplete = (a: ChoreAssignment) => {
    tada();
    void completeAssignment.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId });
  };

  const handleSkip = (a: ChoreAssignment) => {
    void skipAssignment.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId });
  };

  const handlePassTo = (recipientId: string) => {
    const a = passFor;
    if (!a) return;
    tapLight();
    setPassFor(null);
    void share.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId, recipientId });
  };

  const handleCancelShare = (a: ChoreAssignment) => {
    void cancelShare.mutate({ householdId: a.householdId, dueDate: a.dueDate, assignmentId: a.assignmentId });
  };

  // Resolve a member's display name within the household members we know about.
  const nameOf = (userId?: string) => {
    if (!userId) return 'a housemate';
    const m = members?.find((mm) => mm.userId === userId);
    return m?.memberName || m?.email?.split('@')[0] || 'a housemate';
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    // The task is created in the household the chosen chore belongs to.
    const def = defs.find((d) => d.choreDefId === choreDefId);
    if (!def) return;
    await createAssignment.mutateAsync({
      householdId: def.householdId,
      choreDefId,
      assigneeId: user?.sub ?? '',
      dueDate,
    });
    setChoreDefId('');
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setChoreDefId('');
  };

  if (!firstHouseholdId) {
    return (
      <div className="page">
        <NoHouseholdEmpty />
      </div>
    );
  }

  const filterLabels: { value: AssignmentStatus | ''; label: string }[] = [
    { value: '', label: 'All' },
    { value: AssignmentStatus.PENDING, label: 'Pending' },
    { value: AssignmentStatus.COMPLETED, label: 'Completed' },
  ];

  const householdMap = new Map((households ?? []).map((h) => [h.householdId, h.name]));
  const multiHousehold = (households?.length ?? 0) > 1;

  // Only this week's tasks, reading Monday → Sunday.
  const sorted = (assignments ?? [])
    .filter((a) => a.dueDate >= weekStart && a.dueDate <= weekEnd)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  function groupByHousehold(items: ChoreAssignment[]): Array<{ label: string; items: ChoreAssignment[] }> {
    const groups = new Map<string, ChoreAssignment[]>();
    for (const a of items) {
      const group = groups.get(a.householdId) ?? [];
      group.push(a);
      groups.set(a.householdId, group);
    }
    return [...groups.entries()].map(([hId, hItems]) => ({
      label: householdMap.get(hId) ?? hId,
      items: hItems,
    }));
  }

  // Skip permission follows each assignment's own household, not the selected one.
  const allowSkipFor = (a: ChoreAssignment) =>
    settingsById.get(a.householdId)?.allowSkipping !== false;

  const renderTile = (a: ChoreAssignment) => {
    const priority = a.priority ?? ChorePriority.SHOULD_DO;
    const length = a.length ?? ChoreLength.MEDIUM;
    const pts = calcPoints(priority, length);
    const due = dueLabel(a.dueDate);

    switch (a.status) {
      case AssignmentStatus.PENDING:
        return (
          <ChoreTile
            key={a.assignmentId}
            title={a.choreName}
            priority={priority}
            points={pts}
            overdue={due.late}
            disabled={completeAssignment.isPending || skipAssignment.isPending}
            onComplete={() => handleComplete(a)}
            onSkip={allowSkipFor(a) ? () => handleSkip(a) : undefined}
            onOpen={() => { tapLight(); setMenuFor(a); }}
            meta={
              <>
                <span>{LENGTH_META[length].label}</span>
                <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
              </>
            }
          />
        );
      case AssignmentStatus.COMPLETED:
        return (
          <ChoreTile
            key={a.assignmentId}
            title={a.choreName}
            priority={priority}
            points={pts}
            done
            meta={<span>{relativeDueDate(a.dueDate)}</span>}
          />
        );
      case AssignmentStatus.SHARED:
        return (
          <ChoreTile
            key={a.assignmentId}
            title={a.choreName}
            icon="hourglass-outline"
            points={pts}
            meta={
              <>
                <span>Passed to {nameOf(a.sharedWith)}</span>
                <span>{relativeDueDate(a.dueDate)}</span>
              </>
            }
            actions={
              <button className="btn btn-sm btn-ghost" onClick={() => handleCancelShare(a)} disabled={cancelShare.isPending}>
                Cancel
              </button>
            }
          />
        );
      default: // MISSED
        return (
          <ChoreTile
            key={a.assignmentId}
            title={a.choreName}
            icon="play-skip-forward"
            meta={
              <>
                <span>Skipped</span>
                <span>{relativeDueDate(a.dueDate)}</span>
              </>
            }
          />
        );
    }
  };

  const renderGroups = () => {
    if (!multiHousehold) return <div className="chore-tiles">{sorted.map(renderTile)}</div>;
    const groups = groupByHousehold(sorted);
    return groups.map(({ label, items }) => (
      <div key={label} className="chore-group">
        <p className="section-label">{label}</p>
        <div className="chore-tiles">{items.map(renderTile)}</div>
      </div>
    ));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Tasks</h1>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            // Creating while browsing a future week? Land the task in that week.
            if (weekStart > thisMonday) setDueDate(weekStart);
            setShowModal(true);
          }}
        >
          + New task
        </button>
      </div>

      <div className="week-pager">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => { tapLight(); setWeekStart((w) => addDays(w, -7)); }}
          aria-label="Previous week"
        >
          ‹
        </button>
        <button
          className="week-pager-label"
          onClick={() => { tapLight(); setWeekStart(thisMonday); }}
          disabled={isCurrentWeek}
          title={isCurrentWeek ? undefined : 'Back to this week'}
        >
          {weekTitle}
          {/* The far-week label already is the range — don't repeat it. */}
          {weekTitle !== weekRange && <span className="week-pager-sub">{weekRange}</span>}
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => { tapLight(); setWeekStart((w) => addDays(w, 7)); }}
          aria-label="Next week"
        >
          ›
        </button>
      </div>

      <section>
        <div className="filter-tabs">
          {filterLabels.map(({ value, label }) => (
            <button
              key={value}
              className={`filter-tab${statusFilter === value ? ' filter-tab-active' : ''}`}
              aria-pressed={statusFilter === value}
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading && <p className="loading-note">Loading…</p>}
        {!isLoading && sorted.length === 0 && (
          <EmptyState icon="checkbox-outline" title="Nothing this week">
            {isCurrentWeek ? (
              <>Tasks appear as chores get scheduled — or add a one-off with <strong>New task</strong>.</>
            ) : weekStart < thisMonday ? (
              'No tasks were due this week.'
            ) : (
              'Nothing scheduled here yet — this fills in as the week approaches.'
            )}
          </EmptyState>
        )}
        {!isLoading && sorted.length > 0 && (
          <>
            {renderGroups()}
            <p className="swipe-tip">
              Tip: swipe a task right to complete, left to skip
            </p>
          </>
        )}
      </section>

      {menuFor && (() => {
        const priority = menuFor.priority ?? ChorePriority.SHOULD_DO;
        const length = menuFor.length ?? ChoreLength.MEDIUM;
        const pts = calcPoints(priority, length);
        const due = dueLabel(menuFor.dueDate);
        const pMeta = PRIORITY_META[priority];
        const desc = defs.find((d) => d.choreDefId === menuFor.choreDefId)?.description;
        return (
        <Modal title={menuFor.choreName} onClose={() => setMenuFor(null)}>
          {desc && <p className="chore-sheet-desc">{desc}</p>}
          <p className="chore-meta chore-sheet-meta">
            <span className="chore-meta-item">
              <ion-icon name={pMeta.icon} style={{ color: pMeta.color }} />
              {pMeta.label} do
            </span>
            <span className="chore-meta-item">
              <ion-icon name="timer-outline" />
              {LENGTH_META[length].label}
            </span>
            <span className={due.late ? 'chore-due-late' : undefined}>{due.text}</span>
          </p>
          <div className="sheet-stack">
            <button
              className="sheet-row"
              onClick={() => { const a = menuFor; setMenuFor(null); handleComplete(a); }}
            >
              <span className="sheet-row-icon sheet-row-icon-success"><ion-icon name="checkmark" /></span>
              <span className="sheet-row-text">
                <span className="sheet-row-title">Done!</span>
                <span className="sheet-row-sub">Mark it complete and earn +{pts}</span>
              </span>
            </button>
            <button
              className="sheet-row"
              onClick={() => { const a = menuFor; setMenuFor(null); setPassFor(a); }}
            >
              <span className="sheet-row-icon"><ion-icon name="person-add" /></span>
              <span className="sheet-row-text">
                <span className="sheet-row-title">Pass to a housemate</span>
                <span className="sheet-row-sub">Hand this chore to someone else</span>
              </span>
              <ion-icon name="chevron-forward" className="sheet-row-chevron" />
            </button>
            {allowSkipFor(menuFor) && (
              <button
                className="sheet-row"
                onClick={() => { const a = menuFor; setMenuFor(null); handleSkip(a); }}
              >
                <span className="sheet-row-icon"><ion-icon name="play-skip-forward" /></span>
                <span className="sheet-row-text">
                  <span className="sheet-row-title">Skip</span>
                  <span className="sheet-row-sub">Mark as missed for today</span>
                </span>
              </button>
            )}
          </div>
        </Modal>
        );
      })()}

      {passFor && (
        <Modal title="Pass to…" onClose={() => setPassFor(null)}>
          {(pickerMembers ?? []).filter((m) => m.userId !== user?.sub).length === 0 ? (
            <p className="modal-note">
              No other members to pass to. Invite a housemate from the Household page first.
            </p>
          ) : (
            <div className="sheet-stack">
              {(pickerMembers ?? [])
                .filter((m) => m.userId !== user?.sub)
                .map((m) => (
                  <button key={m.userId} className="sheet-row" onClick={() => handlePassTo(m.userId)}>
                    <MemberAvatar member={m} size="2.25rem" fontSize="0.9rem" />
                    <span className="sheet-row-text">
                      <span className="sheet-row-title">{m.memberName || m.email}</span>
                    </span>
                    <ion-icon name="chevron-forward" className="sheet-row-chevron" />
                  </button>
                ))}
            </div>
          )}
        </Modal>
      )}

      {showModal && (
        <Modal title="New task" onClose={closeModal}>
          <form onSubmit={(e) => void handleCreate(e)}>
            <div className="field">
              <label htmlFor="assign-chore">Chore</label>
              <select
                id="assign-chore"
                value={choreDefId}
                onChange={(e) => setChoreDefId(e.target.value)}
                required
                autoFocus
              >
                <option value="">— select a chore —</option>
                {defs.map((d) => (
                  <option key={d.choreDefId} value={d.choreDefId}>
                    {multiHousehold
                      ? `${d.name} — ${householdMap.get(d.householdId) ?? ''}`
                      : d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="assign-date">Due date</label>
              <input
                id="assign-date"
                type="date"
                className="input-inline"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              <button
                type="submit"
                disabled={createAssignment.isPending}
                className="btn btn-primary btn-full"
              >
                {createAssignment.isPending ? 'Adding…' : 'Add task'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
